import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { captureApplicationError } from '@/lib/monitoring';
import { useAuthStore } from '@/stores/authStore';

export interface PendingInvite {
  id: string;
  token: string;
  organizationName: string;
  role: string;
  expiresAt: string;
}

/**
 * Invitations waiting for the signed-in person.
 *
 * The invite email carries a link, but a link in an inbox is not the only way someone
 * arrives: they read the email on a phone, sign in on a laptop, and then there has to be
 * something in front of them. Somebody with no organisation sees nothing else at all.
 */
export function useMyPendingInvites() {
  const email = useAuthStore((state) => state.user?.email);

  return useQuery({
    queryKey: ['my-pending-invites', email],
    enabled: !!email,
    queryFn: async (): Promise<PendingInvite[]> => {
      // Row level security already limits this to invitations addressed to them, but a
      // member of an organisation can also read the ones it has sent, so the address is
      // matched here as well. Nobody should be offered somebody else's invitation.
      const { data, error } = await supabase
        .from('invites')
        .select('id, token, invited_role, expires_at, invited_email, status')
        .eq('status', 'PENDING')
        .gt('expires_at', new Date().toISOString())
        .ilike('invited_email', email!);
      if (error) throw error;

      const rows = (data ?? []).filter(
        (row) => row.invited_email?.toLowerCase() === email!.toLowerCase(),
      );

      // The organisation's name is not readable from the invite itself by somebody who
      // does not work there yet. The invite context answers for a token they hold.
      const invites = await Promise.all(
        rows.map(async (row) => {
          const { data: context, error: contextError } = await supabase.rpc(
            'get_member_invite_context',
            { p_token: row.token },
          );

          if (contextError) {
            captureApplicationError(contextError, {
              tags: { area: 'invites', operation: 'pending_invite_context' },
            });
            return null;
          }

          const resolved = context as {
            ok?: boolean;
            status?: string;
            organizationName?: string;
            invitedRole?: string;
          } | null;

          if (!resolved?.ok || resolved.status !== 'PENDING') return null;

          return {
            id: row.id,
            token: row.token,
            organizationName: resolved.organizationName ?? 'An organisation',
            role: resolved.invitedRole ?? row.invited_role,
            expiresAt: row.expires_at,
          } satisfies PendingInvite;
        }),
      );

      return invites.filter((invite): invite is PendingInvite => invite !== null);
    },
  });
}
